/**
 * System Lizard - Custom 3D Eurorack Rotary Knob Component
 */

export class RotaryKnob {
  constructor(element, options = {}) {
    this.el = element;
    this.min = options.min ?? 0;
    this.max = options.max ?? 1;
    this.value = options.value ?? (this.min + this.max) / 2;
    this.defaultValue = this.value;
    this.step = options.step ?? 0.01;
    this.label = options.label || '';
    this.unit = options.unit || '';
    this.onChange = options.onChange || null;

    // Angle range: -135deg to +135deg (270 degree sweep)
    this.minAngle = -135;
    this.maxAngle = 135;

    this.isDragging = false;
    this.startY = 0;
    this.startValue = this.value;

    this.render();
    this.attachEvents();
    this.update();
  }

  render() {
    this.el.classList.add('eurorack-knob-container');
    this.el.innerHTML = `
      <div class="knob-label">${this.label}</div>
      <div class="knob-cap" tabindex="0">
        <div class="knob-indicator"></div>
      </div>
      <div class="knob-value-display">--</div>
    `;
    this.capEl = this.el.querySelector('.knob-cap');
    this.indicatorEl = this.el.querySelector('.knob-indicator');
    this.valueEl = this.el.querySelector('.knob-value-display');
  }

  attachEvents() {
    const onPointerDown = (e) => {
      this.isDragging = true;
      this.startY = e.clientY || e.touches?.[0]?.clientY || 0;
      this.startValue = this.value;
      document.body.style.cursor = 'ns-resize';
      this.capEl.classList.add('active');

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    };

    const onPointerMove = (e) => {
      if (!this.isDragging) return;
      const currentY = e.clientY || e.touches?.[0]?.clientY || 0;
      const deltaY = this.startY - currentY;
      const sensitivity = e.shiftKey ? 0.001 : 0.005;

      const range = this.max - this.min;
      let newVal = this.startValue + deltaY * range * sensitivity;
      newVal = Math.max(this.min, Math.min(this.max, newVal));

      if (this.step) {
        newVal = Math.round(newVal / this.step) * this.step;
      }

      if (newVal !== this.value) {
        this.setValue(newVal);
      }
    };

    const onPointerUp = () => {
      this.isDragging = false;
      document.body.style.cursor = '';
      this.capEl.classList.remove('active');
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    this.capEl.addEventListener('pointerdown', onPointerDown);

    // Double click reset
    this.capEl.addEventListener('dblclick', () => {
      this.setValue(this.defaultValue);
    });

    // Keyboard support (Arrow keys)
    this.capEl.addEventListener('keydown', (e) => {
      let delta = 0;
      const rangeStep = (this.max - this.min) * (e.shiftKey ? 0.01 : 0.05);
      if (e.key === 'ArrowUp' || e.key === 'ArrowRight') delta = rangeStep;
      if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') delta = -rangeStep;

      if (delta !== 0) {
        e.preventDefault();
        this.setValue(Math.max(this.min, Math.min(this.max, this.value + delta)));
      }
    });
  }

  setValue(val) {
    this.value = val;
    this.update();
    if (this.onChange) {
      this.onChange(this.value);
    }
  }

  update() {
    const fraction = (this.value - this.min) / (this.max - this.min);
    const angle = this.minAngle + fraction * (this.maxAngle - this.minAngle);
    this.capEl.style.transform = `rotate(${angle}deg)`;

    // Format display string
    let displayStr = this.value.toFixed(this.value < 10 && this.value % 1 !== 0 ? 2 : 0);
    if (this.unit) displayStr += ` ${this.unit}`;
    this.valueEl.textContent = displayStr;
  }
}
